import JsBarcode from 'jsbarcode'
import brand from '@/branding'

// Epson OmniLink TM-T88VII: 80 mm roll, about 72 mm of printable width.

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function formatQuantity(value) {
  const num = Number(value)
  if (Number.isNaN(num)) return ''
  return num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatMoney(value) {
  const num = Number(value)
  if (Number.isNaN(num)) return ''
  return `$${num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatDate(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
}

function logoSrc() {
  const src = brand.topbarLogo || ''
  if (!src || /^(data:|https?:|blob:)/.test(src)) return src
  try {
    return new URL(src, window.location.href).href
  } catch (error) {
    return src
  }
}

function barcodeMarkup(code) {
  if (!code) return ''
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  document.body.appendChild(svg)
  try {
    JsBarcode(svg, String(code), {
      format: 'CODE128',
      width: 1.4,
      height: 46,
      displayValue: true,
      fontSize: 12,
      margin: 0,
      background: '#ffffff',
      lineColor: '#000000',
    })
    return svg.outerHTML
  } finally {
    svg.remove()
  }
}

function printTicket({ title, heading, code, rows, items, total, note, signs }) {
  const printWindow = window.open('', '_blank', 'width=320,height=720')
  if (!printWindow) return
  const logo = logoSrc()
  const meta = (rows || []).map(([label, value]) => `
    <div class="meta-row">
      <span>${escapeHtml(label)}</span>
      <span>${escapeHtml(value || '—')}</span>
    </div>
  `).join('')
  const lines = (items || []).map((item) => `
    <div class="item">
      <div class="item-top">
        <span>${escapeHtml(item.code || '')}</span>
        <span>${escapeHtml(item.qty || '')}</span>
      </div>
      <div class="item-name">${escapeHtml(item.name || '')}</div>
      ${item.amount ? `<div class="item-amt">${escapeHtml(item.amount)}</div>` : ''}
    </div>
  `).join('')
  const signRow = (signs || []).map((label) => `<div class="sign">${escapeHtml(label)}</div>`).join('')
  const printScript = '<scr' + 'ipt>'
    + 'window.onload = function () {'
    + '  var images = Array.prototype.slice.call(document.images);'
    + '  var pending = images.filter(function (img) { return !img.complete; });'
    + '  if (!pending.length) { window.print(); return; }'
    + '  var left = pending.length;'
    + '  function done() { if (--left <= 0) window.print(); }'
    + '  pending.forEach(function (img) { img.onload = done; img.onerror = done; });'
    + '};'
    + '</scr' + 'ipt>'

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title)}</title>
        <style>
          @page { size: 80mm auto; margin: 2mm 3.5mm; }
          * { box-sizing: border-box; }
          html, body {
            width: 72mm;
            margin: 0;
            padding: 0;
            color: #000;
            background: #fff;
            font-family: "Courier New", Courier, monospace;
            font-size: 13px;
            font-weight: 700;
            line-height: 1.25;
            -webkit-font-smoothing: none;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            text-shadow: 0.25px 0 0 #000, -0.25px 0 0 #000;
          }
          .center { text-align: center; }
          .logo { display: block; height: 12mm; width: auto; max-width: 42mm; margin: 0 auto 1mm; object-fit: contain; filter: grayscale(1) contrast(2); }
          .brand { margin: 0; font-size: 15px; font-weight: 900; letter-spacing: 0.03em; text-transform: uppercase; }
          h1 { margin: 2mm 0 0; font-size: 15px; font-weight: 900; letter-spacing: 0.04em; }
          .rule { border: 0; border-top: 2px solid #000; margin: 2mm 0; }
          .barcode { margin: 1mm 0 2mm; }
          .barcode svg { width: 100%; height: auto; }
          .meta-row, .item-top { display: flex; justify-content: space-between; gap: 2mm; }
          .meta-row span:last-child, .item-amt { text-align: right; }
          .item { margin-bottom: 1.5mm; }
          .item-name { font-weight: 900; }
          .total { display: flex; justify-content: space-between; font-size: 16px; font-weight: 900; }
          .note { margin: 2mm 0 0; font-size: 12px; font-weight: 700; }
          .sign { margin-top: 8mm; border-top: 2px solid #000; padding-top: 1mm; text-align: center; font-size: 12px; font-weight: 700; }
          .feed { height: 8mm; }
        </style>
      </head>
      <body>
        <div class="center">
          ${logo ? `<img class="logo" src="${escapeHtml(logo)}" alt="" />` : ''}
          <p class="brand">${escapeHtml(brand.displayName || '')}</p>
          <h1>${escapeHtml(heading)}</h1>
        </div>
        <div class="barcode">${barcodeMarkup(code)}</div>
        <hr class="rule" />
        ${meta}
        ${lines ? `<hr class="rule" />${lines}` : ''}
        ${total ? `<hr class="rule" /><div class="total"><span>TOTAL</span><span>${escapeHtml(total)}</span></div>` : ''}
        ${note ? `<p class="note">${escapeHtml(note)}</p>` : ''}
        ${signRow}
        <div class="feed"></div>
        ${printScript}
      </body>
    </html>
  `)
  printWindow.document.close()
}

function partLine(item, withMoney) {
  const qty = [formatQuantity(item.quantity), item.part_unit_of_measure || ''].filter(Boolean).join(' ')
  return {
    code: item.part_barcode,
    name: item.part_name,
    qty,
    amount: withMoney ? formatMoney(item.subtotal) : '',
  }
}

export function printRequisitionTicket(requisition) {
  if (!requisition) return
  printTicket({
    title: `Ticket ${requisition.requisition_number || ''}`,
    heading: 'REQUISICION',
    code: requisition.barcode || requisition.requisition_number,
    rows: [
      ['Folio', requisition.requisition_number],
      ['Fecha', formatDate(requisition.created_at)],
      ['Elaboró', requisition.created_by_name],
      ['Orden', requisition.maintenance_order_number],
      ['Unidad', requisition.unit_short_id],
      ['Estatus', requisition.status_label],
    ],
    items: (requisition.items || []).map((item) => partLine(item, false)),
    note: requisition.comments || '',
    signs: ['Solicita', 'Entrega almacén'],
  })
}

export function printEntryVoucherTicket(voucher) {
  if (!voucher) return
  const rows = [
    ['Folio', voucher.voucher_number],
    ['Fecha', formatDate(voucher.created_at)],
    ['Autorizó', voucher.authorized_by_name],
    ['Compró', voucher.purchased_by_name],
    ['Proveedor', voucher.supplier],
  ]
  if (voucher.purchase_order_number) rows.push(['Orden compra', voucher.purchase_order_number])
  printTicket({
    title: `Ticket ${voucher.voucher_number || ''}`,
    heading: 'VALE DE ENTRADA',
    code: voucher.voucher_number,
    rows,
    items: (voucher.items || []).map((item) => partLine(item, true)),
    total: formatMoney(voucher.total),
    note: voucher.comments || '',
    signs: ['Recibe almacén'],
  })
}

export function printExitVoucherTicket(voucher) {
  if (!voucher) return
  printTicket({
    title: `Ticket ${voucher.voucher_number || ''}`,
    heading: 'VALE DE SALIDA',
    code: voucher.voucher_number,
    rows: [
      ['Folio', voucher.voucher_number],
      ['Fecha', formatDate(voucher.created_at)],
      ['Autorizó', voucher.authorized_by_name],
      ['Recibió', voucher.received_by_name],
      ['Orden', voucher.maintenance_order_number || 'Sin vincular'],
      ['Requisición', voucher.requisition_number || 'Sin requisición'],
    ],
    items: (voucher.items || []).map((item) => partLine(item, true)),
    total: formatMoney(voucher.total),
    note: voucher.comments || '',
    signs: ['Recibe', 'Entrega almacén'],
  })
}

export function printPurchaseOrderTicket(order) {
  if (!order) return
  printTicket({
    title: `Ticket ${order.order_number || ''}`,
    heading: 'ORDEN DE COMPRA',
    code: order.order_number,
    rows: [
      ['Folio', order.order_number],
      ['Fecha', formatDate(order.created_at)],
      ['Proveedor', order.provider_name],
      ['RFC', order.provider_rfc],
      ['Solicita', order.requested_by_name],
      ['Aprueba', order.approved_by_name],
      ['Entrega', order.expected_on],
      ['Estatus', order.status_label],
    ],
    items: (order.items || []).map((item) => ({
      code: item.part_barcode,
      name: item.part_name,
      qty: [formatQuantity(item.quantity), item.part_unit_of_measure || ''].filter(Boolean).join(' '),
      amount: formatMoney(item.unit_cost),
    })),
    note: order.comments || '',
    signs: ['Autoriza', 'Compras'],
  })
}

export function printShelfTicket(shelf) {
  if (!shelf) return
  printTicket({
    title: `Ticket ${shelf.code || ''}`,
    heading: 'ESTANTE',
    code: shelf.code,
    rows: [
      ['Código', shelf.code],
      ['Nombre', shelf.name],
      ['Tipo', shelf.kind === 'single' ? 'Lugar único' : 'Estante'],
    ],
    items: [],
  })
}

export function printPartTicket(part) {
  if (!part) return
  printTicket({
    title: `Ticket ${part.barcode || ''}`,
    heading: 'PIEZA',
    code: part.barcode,
    rows: [
      ['Código', part.barcode],
      ['Nombre', part.name],
      ['Uso', part.recipient_brand_name],
      ['Ubicación', part.location],
      ['Existencia', formatQuantity(part.current_stock)],
    ],
    items: [],
  })
}

export function printToolLoanTicket(loan) {
  if (!loan) return
  printTicket({
    title: `Ticket ${loan.voucher_number || ''}`,
    heading: 'PRESTAMO DE HERRAMIENTA',
    code: loan.barcode || loan.voucher_number,
    rows: [
      ['Folio', loan.voucher_number],
      ['Entrega', formatDate(loan.delivered_at)],
      ['Entregó', loan.delivered_by_name],
      ['Recibe', loan.received_by_name],
      ['Devolver', formatDate(loan.expected_return_at)],
      ['Devuelta', formatDate(loan.returned_at)],
      ['Orden', loan.maintenance_order_number],
      ['Estatus', loan.status_label],
    ],
    items: (loan.items || []).map((item) => partLine(item, false)),
    note: loan.comments || '',
    signs: ['Recibe mecánico', 'Devolución'],
  })
}
